import { zodResolver } from "@hookform/resolvers/zod";
import { get } from "lodash";
import { FC, useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { FileUploadInput } from "@/components/sku/file-upload-input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { GalleryTabDataImage } from "@/data/types";
import { useCMSStore } from "@/hooks/use-cms-store";
import { FileType } from "@/lib/uploads";

import { useCMSTabContext } from "../cms-tab-context";
import MediaItem from "../media-item";
import { useGalleryTabContext } from "./gallery-tab-context";

const galleryItemFormSchemaImage = z.object({
  title: z.string(),
  description: z.string(),
  image_url: z.url(),
  is_downloadable: z.boolean(),
});

type Props = {
  item: GalleryTabDataImage;
  itemIndex: number;
};

export const GalleryTabImageItem: FC<Props> = ({ item, itemIndex }) => {
  const { tabIndex } = useCMSTabContext();

  const form = useForm<z.infer<typeof galleryItemFormSchemaImage>>({
    resolver: zodResolver(galleryItemFormSchemaImage),
    defaultValues: {
      title: item.options.title,
      description: item.options.description,
      image_url: item.options.image_url,
      is_downloadable: get(item.options, "is_downloadable", false),
    },
  });

  const onMove = useCMSStore.use.onMoveGalleryItem();
  const onDelete = useCMSStore.use.onDeleteGalleryItem();
  const onUpdate = useCMSStore.use.onUpdateGalleryItem();

  const { canMoveUp, canMoveDown } = useGalleryTabContext();

  useEffect(() => {
    const unsubscribe = form.subscribe({
      formState: { values: true },
      callback: ({ values }) => {
        onUpdate(tabIndex, itemIndex, values);
      },
    });

    return unsubscribe;
  }, [form, onUpdate, tabIndex, itemIndex]);

  const onMoveItem = useCallback(
    (oldIndex: number, newIndex: number) => {
      onMove(tabIndex, oldIndex, newIndex);
    },
    [tabIndex, onMove],
  );

  const handleDelete = useCallback(() => {
    onDelete(tabIndex, itemIndex);
  }, [tabIndex, itemIndex, onDelete]);

  const itemCanMoveUp = useMemo(
    () => canMoveUp(itemIndex),
    [canMoveUp, itemIndex],
  );

  const itemCanMoveDown = useMemo(
    () => canMoveDown(itemIndex),
    [canMoveDown, itemIndex],
  );

  return (
    <MediaItem
      index={itemIndex}
      title={item.options.title}
      description={item.options.description}
      thumbnailUrl={item.options.image_url}
      onDelete={handleDelete}
      mediaType={FileType.IMAGE}
      canMoveUp={itemCanMoveUp}
      canMoveDown={itemCanMoveDown}
      onMove={onMoveItem}
    >
      <Form {...form}>
        <div className="bg-gray-50 flex flex-col gap-3 items-end p-4 xl:pl-32">
          <FormField
            name="image_url"
            control={form.control}
            render={({ field }) => (
              <FormItem className="flex w-full gap-4 items-center">
                <FormLabel className="min-w-24">Image file</FormLabel>
                <FormControl>
                  <FileUploadInput
                    {...field}
                    fileType={FileType.IMAGE}
                    className="flex min-w-0 flex-1"
                    removeDisabled
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            name="title"
            control={form.control}
            render={({ field }) => (
              <FormItem className="flex w-full gap-4 items-center">
                <FormLabel className="min-w-24">Title</FormLabel>
                <FormControl>
                  <Input {...field} className="bg-white flex-1" />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            name="description"
            control={form.control}
            render={({ field }) => (
              <FormItem className="flex w-full gap-4 items-center">
                <FormLabel className="min-w-24">Description</FormLabel>
                <FormControl>
                  <Textarea {...field} className="bg-white flex-1" />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            name="is_downloadable"
            control={form.control}
            render={({ field }) => (
              <FormItem className="flex w-full gap-4 items-center py-4">
                <FormLabel className="min-w-24">Downloadable</FormLabel>
                <FormControl>
                  <div className="flex items-center gap-4">
                    <Switch
                      className="scale-150 ml-2 mr-2"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                    <p className="text-sm text-muted-foreground">
                      Allow consumers to download this file
                    </p>
                  </div>
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </Form>
    </MediaItem>
  );
};
